import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';
import { Coupon } from '../../types/coupon';
import { formatDate } from '../../utils/dateUtils';

interface CouponValidityPeriodProps {
  coupon: Coupon;
}

const CouponValidityPeriod: React.FC<CouponValidityPeriodProps> = ({ coupon }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = new Date(coupon.start_date);
  startDate.setHours(0, 0, 0, 0);
  // end_dateがnullの場合の処理
  if (!coupon.end_date) {
    if (today < startDate) {
      // 開始前
      const daysUntilStart = Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return (
        <Tooltip title={`${formatDate(coupon.start_date)} 〜`} arrow placement="top">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2">
              {daysUntilStart}日後に開始 ( 期限なし )
            </Typography>
            <AccessTimeIcon
              fontSize="small"
              color="action"
              sx={{
                fontSize: '0.875rem',
                opacity: 0.7,
                ml: 0.5
              }}
            />
          </Box>
        </Tooltip>
      );
    } else {
      // 期間中
      const daysElapsed = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      return (
        <Tooltip title={`${formatDate(coupon.start_date)} 〜`} arrow placement="top">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2">
              期限なし ({daysElapsed}日経過)
            </Typography>
            <AccessTimeIcon
              fontSize="small"
              color="action"
              sx={{
                fontSize: '0.875rem',
                opacity: 0.7,
                ml: 0.5
              }}
            />
          </Box>
        </Tooltip>
      );
    }
  }
  const endDate = new Date(coupon.end_date);
  endDate.setHours(0, 0, 0, 0);
  const tooltipContent = `${formatDate(coupon.start_date)} 〜 ${formatDate(coupon.end_date)}`;
  // 無効の場合は通常の表示
  if (coupon.disabled) {
    return (
      <Box>
        <Typography variant="body2">
          {formatDate(coupon.start_date)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          〜 {formatDate(coupon.end_date)}
        </Typography>
      </Box>
    );
  }
  // 期限切れの場合
  if (today > endDate) {
    const daysAgo = Math.floor((today.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
    return (
      <Tooltip title={tooltipContent} arrow placement="top">
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
          <Typography variant="body2">
            {daysAgo === 0 ? <span>今日<span style={{ fontWeight: 'bold' }}>終了</span></span> :
              <span>{daysAgo}日前に<span style={{ fontWeight: 'bold' }}>終了</span></span>}
          </Typography>
          <AccessTimeIcon
            fontSize="small"
            color="action"
            sx={{
              fontSize: '0.875rem',
              opacity: 0.7,
              ml: 0.5
            }}
          />
        </Box>
      </Tooltip>
    );
  }
  // 開始前の場合
  if (today < startDate) {
    const daysUntilStart = Math.ceil((startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2">
          {daysUntilStart}日後に開始 ( {formatDate(coupon.end_date)}まで)
        </Typography>
        <AccessTimeIcon
          fontSize="small"
          color="action"
          sx={{
            fontSize: '0.875rem',
            opacity: 0.7,
            ml: 0.5
          }}
        />
      </Box>
    );
  }
  // 期間内の場合
  const daysRemaining = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
        <Typography variant="body2">
          {daysRemaining === 0 ? (
            '最終日'
          ) : (
            <>
              残り
              <span style={{ fontSize: '1.1em', fontWeight: 'bold' }}>{daysRemaining}</span>
              日
            </>
          )}
        </Typography>
        <AccessTimeIcon
          fontSize="small"
          color="action"
          sx={{
            fontSize: '0.875rem',
            opacity: 0.7,
            ml: 0.5
          }}
        />
      </Box>
    </Tooltip>
  );
};
export default CouponValidityPeriod;
